// Marks a step carries on the photo: areas (A1..), lines (L1..), labels (R1..) and
// angles between two lines (D1..), normalized 0..1 of the analyzed frame. Mirrors
// 3d-backend/services/spatial-vision/dimensioning.js.
import {
  normalizeRois,
  roiSummary,
  roiToSvg,
  translateRoi,
} from './roiGeometry.js';

const PREFIX = { area: 'A', line: 'L', point: 'R', angle: 'D' };
const POINT_RADIUS = 6;
const MEASURES = {
  distance: { references: 2 },
  angle: { references: 2, linesOnly: true },
  diameter: { references: 1, areasOnly: true },
};
const clamp01 = value => Math.min(1, Math.max(0, Number(value) || 0));
const round4 = value => Math.round(value * 1e4) / 1e4;

export const MEASURE_TYPES = [
  { id: 'distance', label: 'Distance', unit: 'mm' },
  { id: 'angle', label: 'Angle', unit: 'deg' },
  { id: 'diameter', label: 'Diameter', unit: 'mm' },
];

export function nextMarkId(marks, kind) {
  const used = new Set((marks || []).map(mark => mark.id));
  let number = 1;
  while (used.has(`${PREFIX[kind]}${number}`)) number += 1;
  return `${PREFIX[kind]}${number}`;
}

export function pointFromClick(point) {
  return { x: round4(clamp01(point.x)), y: round4(clamp01(point.y)) };
}

export function lineFromDrag(start, end) {
  const line = {
    x1: round4(clamp01(start.x)),
    y1: round4(clamp01(start.y)),
    x2: round4(clamp01(end.x)),
    y2: round4(clamp01(end.y)),
  };
  if (line.x1 === line.x2 && line.y1 === line.y2) return null;
  return line;
}

// Dragging a mark moves it whole: an area delegates to the shape, a line keeps its
// length by clamping the delta against whichever end reaches the edge first, and an
// angle has no geometry of its own, it is drawn from the two lines it names.
// `aspect` is width / height of the frame, which a circle needs to stay on it.
export function translateMark(mark, dx, dy, aspect) {
  if (mark?.kind === 'area') {
    return { ...mark, shape: translateRoi(mark.shape, dx, dy, aspect) };
  }
  if (mark?.kind === 'line' && mark.shape) {
    const { x1, y1, x2, y2 } = mark.shape;
    const stepX = Math.min(
      1 - Math.max(x1, x2),
      Math.max(-Math.min(x1, x2), dx),
    );
    const stepY = Math.min(
      1 - Math.max(y1, y2),
      Math.max(-Math.min(y1, y2), dy),
    );
    return {
      ...mark,
      shape: {
        x1: round4(x1 + stepX),
        y1: round4(y1 + stepY),
        x2: round4(x2 + stepX),
        y2: round4(y2 + stepY),
      },
    };
  }
  if (mark?.kind === 'point' && mark.shape) {
    return {
      ...mark,
      shape: {
        x: round4(clamp01(mark.shape.x + dx)),
        y: round4(clamp01(mark.shape.y + dy)),
      },
    };
  }
  return mark;
}

export function markToSvg(mark, imageWidth, imageHeight) {
  if (mark.kind === 'angle') return null;
  if (mark.kind === 'point') {
    return {
      tag: 'circle',
      attrs: {
        cx: mark.shape.x * imageWidth,
        cy: mark.shape.y * imageHeight,
        r: POINT_RADIUS,
      },
    };
  }
  if (mark.kind === 'line') {
    const { shape } = mark;
    return {
      tag: 'line',
      attrs: {
        x1: shape.x1 * imageWidth,
        y1: shape.y1 * imageHeight,
        x2: shape.x2 * imageWidth,
        y2: shape.y2 * imageHeight,
      },
    };
  }
  return roiToSvg(mark.shape, imageWidth, imageHeight);
}

// An angle has no shape of its own: its name sits between the two lines it measures.
export function angleAnchor(mark, marks, imageWidth, imageHeight) {
  const lines = (mark.lines || [])
    .map(id => (marks || []).find(item => item.id === id))
    .filter(item => item && item.kind === 'line');
  if (!lines.length) return null;
  const mids = lines
    .map(line => markLabelAnchor(line, imageWidth, imageHeight))
    .filter(Boolean);
  if (!mids.length) return null;
  return {
    x: mids.reduce((sum, point) => sum + point.x, 0) / mids.length,
    y: mids.reduce((sum, point) => sum + point.y, 0) / mids.length,
  };
}

export function markLabelAnchor(mark, imageWidth, imageHeight) {
  // An angle carries no shape of its own: where its name sits is angleAnchor's
  // answer, and that needs the two lines it is drawn from, which this does not have.
  if (!mark?.shape) return null;
  const { shape } = mark;
  if (mark.kind === 'point') {
    return { x: shape.x * imageWidth, y: shape.y * imageHeight };
  }
  if (mark.kind === 'line') {
    return {
      x: ((shape.x1 + shape.x2) / 2) * imageWidth,
      y: ((shape.y1 + shape.y2) / 2) * imageHeight,
    };
  }
  if (shape.type === 'rect') {
    return { x: shape.x * imageWidth, y: shape.y * imageHeight };
  }
  if (shape.type === 'circle') {
    return {
      x: (shape.cx - shape.r) * imageWidth,
      y: (shape.cy - shape.r) * imageHeight,
    };
  }
  const xs = shape.points.map(([x]) => x);
  const ys = shape.points.map(([, y]) => y);
  return { x: Math.min(...xs) * imageWidth, y: Math.min(...ys) * imageHeight };
}

// The words of the palette: a mark is called what the tool that drew it is called.
const KIND_WORDS = { line: 'Line', point: 'Label' };
const AREA_WORDS = { rect: 'Block', circle: 'Circle', polygon: 'Polygon' };

export function shapeWord(shape) {
  return AREA_WORDS[shape?.type] || roiSummary(shape);
}

export function markSummary(mark) {
  const parts = [mark.id];
  const name = mark.label || mark.type;
  if (name) parts.push(name);
  if (mark.kind === 'angle')
    parts.push(`Angle ${(mark.lines || []).join('–')}`);
  else parts.push(KIND_WORDS[mark.kind] || shapeWord(mark.shape));
  return parts.join(' · ');
}

export function lineMarkIds(marks) {
  return (marks || [])
    .filter(mark => mark.kind === 'line')
    .map(mark => mark.id);
}

// Removing a line takes the angles that used it along, as on a reference.
export function withoutMark(marks, markId) {
  return (marks || []).filter(
    mark => mark.id !== markId && !(mark.lines || []).includes(markId),
  );
}

// A Block, Circle or Polygon drawn on the photo says where A1 is. It does not say what
// to look at: that is what the prompt says, and "inside A1" or "outside A1" is resolved
// from the step's marks by name, so a condition that names no mark searches the whole
// frame. The simple editor used to copy these shapes into condition.roi as well, and the
// copy cut every finding a condition never asked to be cut: "find rust" lost everything
// outside the drawn areas, and "find cracks outside A1" could match nothing at all,
// because the copy demanded inside while the words demanded outside. Nothing writes the
// copy any more; the shape is still built here so that conditionRoiUpdates can recognise
// a copy left in the database by the old build and clear it.
function marksRoi(marks) {
  const shapes = (marks || [])
    .filter(mark => mark.kind === 'area' && mark.shape)
    .map(mark => mark.shape);
  if (!shapes.length) return null;
  return normalizeRois(shapes);
}

// Both sides are rebuilt by normalizeRois, which writes the keys of a shape in one
// fixed order, so the same geometry always prints the same string whichever way it
// reached here: from the marks, or back from the database as a bare shape.
const sameRoi = (a, b) =>
  JSON.stringify(normalizeRois(a)) === JSON.stringify(normalizeRois(b));

// Conditions saved by the old build carry the copy until something takes it off, and
// the simple editor has no control that could: the operator has marks and a prompt.
// Every change to the marks passes through here, so this is where the leftovers go.
// Only a region that still is the copy of these marks is cleared; a region drawn by hand
// in the old form is geometry the marks never had, and the operator meant it.
export function conditionRoiUpdates(conditions, marks) {
  const copied = marksRoi(marks);
  if (!copied) return [];
  return (conditions || [])
    .filter(condition => sameRoi(condition.roi, copied))
    .map(condition => ({ id: condition.id, roi: null }));
}

export function measurementLabel(measurement) {
  if (!measurement || measurement.status === 'unresolved')
    return 'not measured';
  const value =
    measurement.status === 'unscaled'
      ? measurement.valuePx
      : measurement.valueMm;
  const verdict = ['pass', 'fail'].includes(measurement.status)
    ? ` ${measurement.status}`
    : '';
  return `${value} ${measurement.unit}${verdict}`;
}

export function referenceOptions(marks, calibrated) {
  const points = calibrated
    ? [
        { id: 'R1', kind: 'point', label: 'R1 (reference point)' },
        { id: 'R2', kind: 'point', label: 'R2 (reference point)' },
      ]
    : [];
  return [
    ...points,
    ...(marks || []).map(mark => ({
      id: mark.id,
      kind: mark.kind,
      label: markSummary(mark),
    })),
  ];
}

function referenceKind(reference, marks) {
  if (reference === 'R1' || reference === 'R2') return 'point';
  return (marks || []).find(mark => mark.id === reference)?.kind || null;
}

export function validMeasurement(spec, marks) {
  const rule = MEASURES[spec?.measure];
  if (!rule) return false;
  const fromKind = referenceKind(spec.from, marks);
  const toKind = rule.references === 2 ? referenceKind(spec.to, marks) : 'none';
  if (!fromKind || !toKind) return false;
  if (rule.linesOnly && (fromKind !== 'line' || toKind !== 'line'))
    return false;
  if (rule.areasOnly && fromKind !== 'area') return false;
  return true;
}

// While a mark is dragged, its preview is written as an SVG transform onto the nodes
// already drawn. The label of an angle drawn from the moving line has to travel with
// it, but not as far as the line does: the anchor of an angle is the mean of the
// midpoints of its two lines, so a line moved by d carries it d/2. Each drawn mark
// gets its own shift, measured from where its anchor lands once the move is saved.
export function markPreviewShifts(mark, moved, marks, imageWidth, imageHeight) {
  const shiftBetween = (id, before, after) =>
    before && after
      ? [{ id, dx: after.x - before.x, dy: after.y - before.y }]
      : [];
  const saved = (marks || []).map(item => (item.id === mark.id ? moved : item));
  return [
    ...shiftBetween(
      mark.id,
      markLabelAnchor(mark, imageWidth, imageHeight),
      markLabelAnchor(moved, imageWidth, imageHeight),
    ),
    ...(marks || [])
      .filter(
        item => item.kind === 'angle' && (item.lines || []).includes(mark.id),
      )
      .flatMap(item =>
        shiftBetween(
          item.id,
          angleAnchor(item, marks, imageWidth, imageHeight),
          angleAnchor(item, saved, imageWidth, imageHeight),
        ),
      ),
  ];
}
